import React, { useEffect, useState } from "react";
import { Plus, Mail, Phone, MapPin, Trash2, StickyNote, X, DollarSign, Sparkles, Loader2, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { toast } from "sonner";
import {
  fetchClients, createClient, updateClientApi, addClientNoteApi, deleteClientApi, formatDate, summarizeNotes,
} from "../../lib/api";
import { clientStages, services } from "../../mock";
import { useAuth } from "../../context/AuthContext";
import { getClientActivity, pushToDisputeFox } from "../../lib/api";
import { VoiceRecorder } from "../common/VoiceRecorder";
import { ClickToCall } from "../common/ClickToCall";
import SummaryView from "../common/SummaryView";
import ActivityTimeline from "../common/ActivityTimeline";

const stageColor = (s) =>
  ({
    Lead: "bg-blue-100 text-blue-700",
    Onboarding: "bg-amber-100 text-amber-700",
    Active: "bg-green-100 text-green-700",
    Closed: "bg-gray-100 text-gray-500",
  }[s] || "bg-gray-100 text-gray-600");

export default function Clients() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [clientActivity, setClientActivity] = useState([]);

  useEffect(() => {
    setSummary("");
    setClientActivity([]);
    if (active?.email && token) {
      getClientActivity(token, active.email).then(setClientActivity).catch(() => setClientActivity([]));
    }
  }, [active, token]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", stage: "Onboarding", value: 0 });

  const refresh = async () => {
    const list = await fetchClients();
    setClients(list);
    setActive((a) => (a ? list.find((c) => c.id === a.id) || null : null));
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleAdd = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    await createClient({ ...form, value: Number(form.value) || 0 });
    await refresh();
    setOpen(false);
    setForm({ name: "", email: "", phone: "", location: "", stage: "Onboarding", value: 0 });
    toast.success("Client added");
  };

  const changeStage = async (id, stage) => {
    await updateClientApi(id, { stage });
    await refresh();
    toast.success(`Stage updated to ${stage}`);
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    await addClientNoteApi(active.id, note.trim());
    setNote("");
    await refresh();
    toast.success("Note added");
  };

  const summarizeClientNotes = async () => {
    const text = (active?.notes || []).map((n) => n.text).join("\n\n");
    if (!text.trim()) { toast.error("No notes to summarize yet."); return; }
    setSummarizing(true);
    try {
      const res = await summarizeNotes(token, { mode: "current", text });
      setSummary(res.summary);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not summarize");
    } finally {
      setSummarizing(false);
    }
  };

  const removeClient = async (id) => {
    await deleteClientApi(id);
    setActive(null);
    await refresh();
    toast.success("Client removed");
  };

  const pushClient = async (c) => {
    try {
      await pushToDisputeFox(token, { name: c.name, email: c.email, phone: c.phone || "", service: c.service || "", event: "manual_push_client" });
      toast.success(`${c.name} sent to DisputeFox`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not push to DisputeFox");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Clients</h1>
          <p className="text-gray-500 text-sm mt-0.5">{clients.length} clients in your book of business</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-navy hover:bg-navy-light text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Client
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream">
              <tr className="text-left text-gray-500">
                <th className="px-5 py-3.5 font-medium">Client</th>
                <th className="px-5 py-3.5 font-medium hidden md:table-cell">Location</th>
                <th className="px-5 py-3.5 font-medium hidden lg:table-cell">Services</th>
                <th className="px-5 py-3.5 font-medium">Value</th>
                <th className="px-5 py-3.5 font-medium">Stage</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => { setActive(c); setSummary(""); }}
                  className="border-t border-gray-50 hover:bg-cream/50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-navy text-gold font-semibold flex items-center justify-center">
                        {c.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <div className="font-medium text-navy">{c.name}</div>
                        <div className="text-xs text-gray-400">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-600 hidden md:table-cell">{c.location || "—"}</td>
                  <td className="px-5 py-4 text-gray-600 hidden lg:table-cell">
                    {(c.services || []).length ? c.services.join(", ") : "—"}
                  </td>
                  <td className="px-5 py-4 font-medium text-navy">${(c.value || 0).toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${stageColor(c.stage)}`}>
                      {c.stage}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Client Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy">Add New Client</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input placeholder="Location (City, State)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{clientStages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Value ($)" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-navy hover:bg-navy-light text-white">Add Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client detail sheet */}
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-navy text-xl">{active.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-14 w-14 rounded-full bg-navy text-gold text-lg font-semibold flex items-center justify-center">
                  {active.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <Select value={active.stage} onValueChange={(v) => changeStage(active.id, v)}>
                  <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{clientStages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600"><Mail className="h-4 w-4 text-gray-400" /> {active.email}</div>
                {active.phone && <div className="flex items-center gap-2 text-gray-600"><Phone className="h-4 w-4 text-gray-400" /> {active.phone}</div>}
                {active.location && <div className="flex items-center gap-2 text-gray-600"><MapPin className="h-4 w-4 text-gray-400" /> {active.location}</div>}
                <div className="flex items-center gap-2 text-gray-600"><DollarSign className="h-4 w-4 text-gray-400" /> ${(active.value || 0).toLocaleString()} lifetime value</div>
              </div>

              {(active.services || []).length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Services</div>
                  <div className="flex flex-wrap gap-2">
                    {active.services.map((s) => (
                      <span key={s} className="bg-cream text-navy text-xs px-2.5 py-1 rounded-md">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    <StickyNote className="h-4 w-4" /> Notes
                  </div>
                  <div className="flex items-center gap-2">
                    <ClickToCall token={token} toNumber={active.phone} clientId={active.id} clientName={active.name} onTranscript={(t) => setNote((n) => (n ? `${n} ${t}` : t))} />
                    <VoiceRecorder token={token} onText={(t) => setNote((n) => (n ? `${n} ${t}` : t))} label="Speak" />
                    <button
                      onClick={summarizeClientNotes}
                      disabled={summarizing}
                      className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-gold/15 text-gold-dark hover:bg-gold/25 disabled:opacity-50 transition-colors"
                      data-testid="client-summarize-notes"
                    >
                      {summarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Summarize
                    </button>
                  </div>
                </div>
                {summary && (
                  <div className="mb-3 bg-cream rounded-lg p-3.5 border border-gold/20" data-testid="client-notes-summary">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-gold-dark uppercase tracking-wide">AI Summary</span>
                      <button onClick={() => setSummary("")} className="text-gray-400 hover:text-navy"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <SummaryView text={summary} />
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note, or tap Speak to dictate..." rows={2} className="text-sm" data-testid="client-note-input" />
                  <Button onClick={handleAddNote} className="bg-navy hover:bg-navy-light text-white self-end">Add</Button>
                </div>
                <div className="mt-4 space-y-3">
                  {(active.notes || []).map((n) => (
                    <div key={n.id} className="bg-cream rounded-lg p-3">
                      <p className="text-sm text-gray-700">{n.text}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(n.date)}</p>
                    </div>
                  ))}
                  {(active.notes || []).length === 0 && <p className="text-sm text-gray-400">No notes yet.</p>}
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  <StickyNote className="h-4 w-4" /> Activity
                </div>
                <div className="max-h-72 overflow-y-auto pr-1" data-testid="client-activity-timeline">
                  <ActivityTimeline items={clientActivity} emptyText="No recorded activity for this client yet." />
                </div>
              </div>

              <button
                onClick={() => pushClient(active)}
                className="mt-8 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-orange-600 hover:bg-orange-50 text-sm font-medium transition-colors border border-orange-200"
                data-testid="client-push-disputefox"
              >
                <Zap className="h-4 w-4" /> Push to DisputeFox
              </button>
              <button
                onClick={() => removeClient(active.id)}
                className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-red-500 hover:bg-red-50 text-sm font-medium transition-colors"
              >
                <Trash2 className="h-4 w-4" /> Remove Client
              </button>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
